import { Loader2 } from 'lucide-react';
import { useEffect, useState } from 'react';

import { googleLoginUrl } from '@/entities/auth';
import { Button, HintIcon } from '@/shared/ui';

import { AUTH_HINT } from '../texts';

/**
 * «Войти через Google» — обычный переход браузера на бэк, не fetch и не popup:
 * бэк ставит защитную куку и сам отправляет на страницу Google. Кнопка видна
 * всегда, даже если Google ещё не привязан, — привязка случится при первом
 * таком входе.
 */
export function GoogleLoginButton({ disabled }: { disabled?: boolean }) {
  const [leaving, setLeaving] = useState(false);

  // «Назад» со страницы Google достаёт страницу из bfcache вместе с лоадером —
  // снимаем его, иначе кнопка так и останется заблокированной.
  useEffect(() => {
    const onPageShow = (event: PageTransitionEvent) => {
      if (event.persisted) {
        setLeaving(false);
      }
    };

    window.addEventListener('pageshow', onPageShow);
    return () => window.removeEventListener('pageshow', onPageShow);
  }, []);

  return (
    <div className="flex items-center gap-2">
      <Button
        type="button"
        variant="outline"
        size="lg"
        disabled={disabled || leaving}
        onClick={() => {
          setLeaving(true);
          window.location.href = googleLoginUrl();
        }}
        className="flex-1 rounded-xl border-slate-200 bg-white text-slate-700"
      >
        {leaving ? (
          <Loader2 className="h-4 w-4 animate-spin" />
        ) : (
          <GoogleMark />
        )}
        Войти через Google
      </Button>
      <HintIcon text={AUTH_HINT.google} />
    </div>
  );
}

/** Разделитель «или» между входом по паролю и через Google. */
export function OrDivider() {
  return (
    <div className="flex items-center gap-3 text-xs text-slate-400">
      <span className="h-px flex-1 bg-slate-200" />
      или
      <span className="h-px flex-1 bg-slate-200" />
    </div>
  );
}

function GoogleMark() {
  return (
    <svg viewBox="0 0 48 48" aria-hidden className="h-4 w-4">
      <path
        fill="#FFC107"
        d="M43.611,20.083H42V20H24v8h11.303c-1.649,4.657-6.08,8-11.303,8c-6.627,0-12-5.373-12-12c0-6.627,5.373-12,12-12c3.059,0,5.842,1.154,7.961,3.039l5.657-5.657C34.046,6.053,29.268,4,24,4C12.955,4,4,12.955,4,24c0,11.045,8.955,20,20,20c11.045,0,20-8.955,20-20C44,22.659,43.862,21.35,43.611,20.083z"
      />
      <path
        fill="#FF3D00"
        d="M6.306,14.691l6.571,4.819C14.655,15.108,18.961,12,24,12c3.059,0,5.842,1.154,7.961,3.039l5.657-5.657C34.046,6.053,29.268,4,24,4C16.318,4,9.656,8.337,6.306,14.691z"
      />
      <path
        fill="#4CAF50"
        d="M24,44c5.166,0,9.86-1.977,13.409-5.192l-6.19-5.238C29.211,35.091,26.715,36,24,36c-5.202,0-9.619-3.317-11.283-7.946l-6.522,5.025C9.505,39.556,16.227,44,24,44z"
      />
      <path
        fill="#1976D2"
        d="M43.611,20.083H42V20H24v8h11.303c-0.792,2.237-2.231,4.166-4.087,5.571c0.001-0.001,0.002-0.001,0.003-0.002l6.19,5.238C36.971,39.205,44,34,44,24C44,22.659,43.862,21.35,43.611,20.083z"
      />
    </svg>
  );
}
